import React, { useEffect, useRef, useState } from "react";
import NotificationsBell from "./NotificationsBell";

function LogoutIcon(props) {
  return (
    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" {...props}>
      <path d="M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4" />
      <path d="M16 17l5-5-5-5" />
      <path d="M21 12H9" />
    </svg>
  );
}

function useHeaderMenu({ enabled, forceClosed }) {
  const [open, setOpen] = useState(false);
  const wrapRef = useRef(null);

  useEffect(() => {
    if (!enabled || forceClosed) {
      setOpen(false);
    }
  }, [enabled, forceClosed]);

  useEffect(() => {
    if (!open) return undefined;

    const onDoc = (e) => {
      if (!wrapRef.current?.contains(e.target)) {
        setOpen(false);
      }
    };
    const onKey = (e) => {
      if (e.key === "Escape") {
        setOpen(false);
      }
    };

    document.addEventListener("mousedown", onDoc, true);
    document.addEventListener("touchstart", onDoc, true);
    document.addEventListener("keydown", onKey);

    return () => {
      document.removeEventListener("mousedown", onDoc, true);
      document.removeEventListener("touchstart", onDoc, true);
      document.removeEventListener("keydown", onKey);
    };
  }, [open]);

  return { open, setOpen, wrapRef };
}

function HeaderProfileMenu({
  drawer,
  isAuthed,
  setPage,
  canManageModels = false,
  profile,
  onLogout,
}) {
  const { open, setOpen, wrapRef } = useHeaderMenu({ enabled: isAuthed, forceClosed: drawer });

  if (!isAuthed) return null;

  const initial = (profile?.name || "U").slice(0, 1).toUpperCase();
  const handleNavigate = (nextPage) => {
    setOpen(false);
    setPage(nextPage);
  };

  const handleLogout = () => {
    setOpen(false);
    if (typeof onLogout === "function") onLogout();
  };

  return (
    <div className="relative" ref={wrapRef}>
      <button
        type="button"
        onClick={() => setOpen((prev) => !prev)}
        aria-label="Потребителско меню"
        aria-expanded={open}
        className={[
          "flex h-11 w-11 items-center justify-center rounded-full border transition focus:outline-none focus:ring-2 focus:ring-violet-600/40",
          open
            ? "border-violet-500/40 bg-violet-600/15 text-violet-100"
            : "border-[#1f2638] bg-[#121825] text-slate-200 hover:bg-[#141b2d]",
        ].join(" ")}
        title={profile?.name || "Профил"}
      >
        {profile?.picture ? (
          <img
            src={profile.picture}
            alt={profile?.name || "Profile"}
            className="h-9 w-9 rounded-full object-cover"
          />
        ) : (
          <span className="grid h-9 w-9 place-items-center rounded-full bg-violet-600/30 font-semibold">
            {initial}
          </span>
        )}
      </button>

      {open && (
        <div className="absolute right-0 top-full z-50 mt-3 w-64 max-w-[calc(100vw-1rem)] rounded-xl border border-slate-800/60 bg-[#0f1523] p-1 shadow-lg">
          <div className="rounded-lg px-3 py-3 text-left">
            <div className="truncate text-sm font-semibold text-slate-100">{profile?.name || "Потребител"}</div>
            <div className="truncate text-xs text-slate-400">{profile?.email || ""}</div>
          </div>
          <div className="my-1 h-px bg-slate-800/60" />
          {canManageModels && (
            <button
              type="button"
              className="w-full rounded-lg px-3 py-2 text-left text-slate-200 hover:bg-white/5"
              onClick={() => handleNavigate("models")}
            >
              Модели
            </button>
          )}
          <button
            type="button"
            className="w-full rounded-lg px-3 py-2 text-left text-slate-200 hover:bg-white/5"
            onClick={() => handleNavigate("scenarios")}
          >
            Сценарии
          </button>
          <button
            type="button"
            className="w-full rounded-lg px-3 py-2 text-left text-slate-200 hover:bg-white/5"
            onClick={() => handleNavigate("templates")}
          >
            Темплейти
          </button>
          <button
            type="button"
            className="w-full rounded-lg px-3 py-2 text-left text-slate-200 hover:bg-white/5"
            onClick={() => handleNavigate("creditors")}
          >
            Кредитори
          </button>
          <div className="my-1 h-px bg-slate-800/60" />
          <button
            type="button"
            className="flex w-full items-center gap-2 rounded-lg px-3 py-2 text-left text-rose-300 hover:bg-white/5"
            onClick={handleLogout}
          >
            <LogoutIcon className="shrink-0" />
            <span>Излез</span>
          </button>
        </div>
      )}
    </div>
  );
}

export default function AppHeader({
  drawer,
  setDrawer,
  showNotifications = false,
  isAuthed = false,
  setPage,
  canManageModels = false,
  profile = null,
  onLogout,
}) {
  return (
    <header className="sticky top-0 z-30 -mt-6 -mx-6 mb-6 border-b border-slate-800/60 bg-[#0f1523]/80 backdrop-blur md:-mt-8 md:-mx-8">
      <div className="flex h-14 items-center gap-3 px-4 md:px-8">
        <button
          className="btn btn-ghost md:hidden"
          onClick={() => setDrawer((v) => !v)}
          aria-expanded={drawer}
          aria-controls="mobile-sidebar"
          aria-label="Отвори меню"
        >
          <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <path d="M3 6h18M3 12h18M3 18h18" />
          </svg>
        </button>

        <div className="flex-1" />

        <div className="flex items-center gap-3">
          {isAuthed && (
            <NotificationsBell
              enabled={showNotifications}
              forceClosed={drawer}
            />
          )}

          <HeaderProfileMenu
            drawer={drawer}
            isAuthed={isAuthed}
            setPage={setPage}
            canManageModels={canManageModels}
            profile={profile}
            onLogout={onLogout}
          />
        </div>
      </div>
    </header>
  );
}
